
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { createAchievementNotification } from "@/utils/notification-utils";
import { getCurrentLevel } from "../utils/karma-utils";

export const useKarmaProfileUpdate = (
  karmaPoints: number,
  setKarmaPoints: (points: number) => void,
  currentLevel: number,
  setCurrentLevel: (level: number) => void
) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const updateUserProfileWithPoints = async (pointsToAdd: number): Promise<boolean> => {
    if (!user) return false;

    const newKarmaPoints = karmaPoints + pointsToAdd;
    const newLevel = getCurrentLevel(newKarmaPoints);
    const shouldLevelUp = newLevel > currentLevel;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          karma_points: newKarmaPoints,
          karma_level: shouldLevelUp ? newLevel : currentLevel
        })
        .eq('id', user.id);

      if (error) throw error;
      
      setKarmaPoints(newKarmaPoints);
      
      if (shouldLevelUp) {
        setCurrentLevel(newLevel);
        try {
          await createAchievementNotification(user.id, newLevel);
        } catch (err) {
          console.error("Failed to create achievement notification:", err);
        }
        toast({
          title: "Level Up!",
          description: `Congratulations! You've reached level ${newLevel}!`,
        });
      }
      return true; // Profile update successful
    } catch (error) {
      console.error("Failed to update profile, updating local state:", error);
      // Even if profile update fails, still update local state
      setKarmaPoints(newKarmaPoints);
      if (shouldLevelUp) {
        setCurrentLevel(newLevel);
         // Attempt to show level up toast even on local update if DB failed
        toast({
          title: "Level Up! (Locally updated)",
          description: `Congratulations! You've reached level ${newLevel}! Your points will sync later.`,
          variant: "default"
        });
      }
      return false; // Profile update failed
    }
  };

  return { updateUserProfileWithPoints };
};

